import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

const RETRY_DELAYS_MS = [0, 2000, 5000];

export type BackendHealthStatus = 'checking' | 'connected' | 'disconnected';

export function useBackendHealth() {
  const [backendStatus, setBackendStatus] = useState<BackendHealthStatus>('checking');

  const checkBackendHealth = useCallback(async (): Promise<boolean> => {
    try {
      await api.checkHealth();
      setBackendStatus('connected');
      return true;
    } catch {
      setBackendStatus('disconnected');
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkWithRetry() {
      setBackendStatus('checking');
      for (const delay of RETRY_DELAYS_MS) {
        if (cancelled) return;
        if (delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
        if (cancelled) return;
        if (await checkBackendHealth()) return;
      }
      if (!cancelled) setBackendStatus('disconnected');
    }

    void checkWithRetry();
    return () => {
      cancelled = true;
    };
  }, [checkBackendHealth]);

  return { backendStatus, checkBackendHealth };
}
